import { useEffect } from "react";
import { useLanguage } from "@/lib/i18n";
import { homeFaq } from "@/data/homeFaq";
import { APP_STORE_URL, PLAY_STORE_URL, ogDefaultFor } from "@/lib/seo";

const SITE_URL = "https://refoundapp.ca";
const LOGO_URL = `${SITE_URL}/icon.png`;

function upsertMeta(attr: "name" | "property", value: string, content: string) {
  const selector = `meta[${attr}="${value}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, value);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string, hreflang?: string) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(id: string, data: object) {
  const selector = `script[data-seo-id="${id}"]`;
  let el = document.head.querySelector<HTMLScriptElement>(selector);
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-seo-id", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export function HomeSEO() {
  const { language } = useLanguage();

  useEffect(() => {
    const isFr = language === "fr";
    const OG_IMAGE = ogDefaultFor(language);
    const htmlLang = isFr ? "fr-CA" : "en-CA";
    const path = isFr ? "/" : "/en";
    const url = `${SITE_URL}${path}`;
    const urlFr = `${SITE_URL}/`;
    const urlEn = `${SITE_URL}/en`;

    const title = isFr
      ? "ReFound — Reçus intelligents pour les Canadiens | Application de gestion de reçus"
      : "ReFound — Smarter Receipts for Canadians | Receipt Management App";

    const description = isFr
      ? "ReFound enrichit vos reçus canadiens : noms et images de produits, suivi des garanties et aide aux retours. Canadian Tire, Costco, Walmart. Gratuit sur iPhone et Android."
      : "ReFound enriches your Canadian receipts: product names and images, warranty tracking and return support. Canadian Tire, Costco, Walmart and more. Free on iPhone and Android.";

    const keywords = isFr
      ? "application de reçus, gestion de reçus, suivi de garantie, aide aux retours, reçus canadiens, reçus numériques, organisateur de reçus, rappels de garantie, application Android reçus, application iPhone reçus, Google Play, App Store, ReFound, Canadian Tire, Home Depot, Walmart, IKEA, Best Buy, Costco, Dollarama, Tigre Géant, Giant Tiger, BMR, Canac, Décathlon, Decathlon"
      : "receipt app, receipt management, warranty tracker, return assistance, Canadian receipts, digital receipts, receipt organizer, warranty reminders, Android receipt app, iPhone receipt app, Google Play, App Store, ReFound, Canadian Tire, Home Depot, Walmart, IKEA, Best Buy, Costco, Dollarama, Tigre Géant, Giant Tiger, BMR, Canac, Décathlon, Decathlon";

    document.title = title;
    document.documentElement.setAttribute("lang", htmlLang);

    // Standard meta
    upsertMeta("name", "description", description);
    upsertMeta("name", "keywords", keywords);
    upsertMeta("name", "author", "ReFound");
    upsertMeta(
      "name",
      "robots",
      "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
    );

    // AI / generative engines opt-in
    upsertMeta("name", "googlebot", "index, follow");
    upsertMeta("name", "bingbot", "index, follow");

    // Open Graph
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", OG_IMAGE);
    upsertMeta("property", "og:image:alt", "ReFound");
    upsertMeta("property", "og:locale", isFr ? "fr_CA" : "en_CA");
    upsertMeta("property", "og:locale:alternate", isFr ? "en_CA" : "fr_CA");
    upsertMeta("property", "og:site_name", "ReFound");

    // Twitter
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", OG_IMAGE);

    // Canonical + hreflang
    upsertLink("canonical", url);
    upsertLink("alternate", urlFr, "fr-CA");
    upsertLink("alternate", urlEn, "en-CA");
    upsertLink("alternate", urlFr, "x-default");

    // -------- JSON-LD --------

    // 1) Organization
    const organization = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": `${SITE_URL}#organization`,
      name: "ReFound",
      url: SITE_URL,
      logo: { "@type": "ImageObject", url: LOGO_URL },
      email: "info@refoundapp.ca",
      areaServed: "CA",
      sameAs: [],
    };

    // 2) WebSite (with SearchAction for sitelinks searchbox)
    const website = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${SITE_URL}#website`,
      url: SITE_URL,
      name: "ReFound",
      description,
      publisher: { "@id": `${SITE_URL}#organization` },
      inLanguage: [htmlLang, isFr ? "en-CA" : "fr-CA"],
    };

    // 3) SoftwareApplication (mobile app)
    const softwareApp = {
      "@context": "https://schema.org",
      "@type": "MobileApplication",
      "@id": `${SITE_URL}#app`,
      name: "ReFound",
      operatingSystem: "iOS, Android",
      applicationCategory: "ShoppingApplication",
      applicationSubCategory: "Receipt management",
      url: SITE_URL,
      image: OG_IMAGE,
      screenshot: [
        {
          "@type": "ImageObject",
          url: `${SITE_URL}${isFr ? "/scan-fr.png" : "/scan-en.png"}`,
          caption: isFr
            ? "Numérisation d'un reçu papier avec ReFound"
            : "Scanning a paper receipt with ReFound",
        },
        {
          "@type": "ImageObject",
          url: `${SITE_URL}${isFr ? "/mockup-fr.png" : "/mockup-en.png"}`,
          caption: isFr
            ? "Reçu enrichi dans ReFound : noms de produits, images et garanties"
            : "Enriched receipt in ReFound: product names, images and warranties",
        },
      ],
      description,
      countriesSupported: "CA",
      inLanguage: ["fr-CA", "en-CA"],
      downloadUrl: [APP_STORE_URL, PLAY_STORE_URL],
      installUrl: [APP_STORE_URL, PLAY_STORE_URL],
      sameAs: [APP_STORE_URL, PLAY_STORE_URL],
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "CAD",
      },
      author: { "@id": `${SITE_URL}#organization` },
      publisher: { "@id": `${SITE_URL}#organization` },
      featureList: isFr
        ? [
            "Numérisation de reçus",
            "Reçus enrichis (nom et image du produit, catégorie)",
            "Recherche rapide de reçus",
            "Aide aux retours en magasin",
            "Affichage du code-barres du reçu quand disponible",
            "Suivi des garanties et dates limites de retour estimées",
            "Partage de reçus avec un conjoint ou un membre de la famille",
            "Organisation des preuves d'achat",
          ]
        : [
            "Receipt scanning",
            "Enriched receipts (product name and image, category)",
            "Fast receipt search",
            "In-store return assistance",
            "Receipt barcode display when available",
            "Warranty tracking and estimated return deadlines",
            "Family receipt sharing with a partner or household member",
            "Proof of purchase organization",
          ],
    };

    // 4) FAQPage (huge for AEO / AI answers)
    const faqSource = (isFr ? homeFaq.fr : homeFaq.en).map((f) => ({
      question: f.q,
      answer: f.a,
    }));
    const faqPage = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "@id": `${url}#faq`,
      inLanguage: htmlLang,
      mainEntity: faqSource.map((q) => ({
        "@type": "Question",
        name: q.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: q.answer,
        },
      })),
    };

    // 5) WebPage with breadcrumb
    const webPage = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${url}#webpage`,
      url,
      name: title,
      description,
      inLanguage: htmlLang,
      isPartOf: { "@id": `${SITE_URL}#website` },
      about: { "@id": `${SITE_URL}#app` },
      primaryImageOfPage: { "@type": "ImageObject", url: OG_IMAGE },
    };

    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: isFr ? "Accueil" : "Home",
          item: url,
        },
      ],
    };

    upsertJsonLd("home-organization", organization);
    upsertJsonLd("home-website", website);
    upsertJsonLd("home-app", softwareApp);
    upsertJsonLd("home-faq", faqPage);
    upsertJsonLd("home-webpage", webPage);
    upsertJsonLd("home-breadcrumb", breadcrumb);
  }, [language]);

  return null;
}
